import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

// Utils
import { useTranslation } from 'react-i18next';
import {
  endOfYear, format, getYear, startOfYear,
} from 'date-fns';
import { useMediaQuery } from 'react-responsive';
import { useMutation } from 'react-query';
import { omit } from 'lodash';
import { isGranted } from '../../../utils/gate/permissionsGate';
import { useAppContext } from '../../../store/AppContext';
import SCOPE from '../../../utils/gate/scope';
import dayId from '../../../utils/tools/dayId';

// Icons
import { ReactComponent as TrashIcon } from '../../../assets/icons/trash.svg';

// Services
import useSeasonService from '../../../services/seasonService';

// Components
import SkeletonLoader from '../../molecules/SkeletonLoader/SkeletonLoader';
import GenericTable from '../GenericTable/GenericTable';
import EmptyList from '../../organisms/EmptyList/EmptyList';
import SelectField from '../../atoms/SelectField/SelectField';
import DatePickerInput from '../../atoms/DatePickerInput/DatePickerInput';

import styles from './SeasonsTable.module.scss';
import useNotification from '../../../utils/hooks/useNotification';
import useModal from '../../../utils/hooks/useModal';

function SeasonsTable({
  year, seasons, setSeasons, isFetching, seasonOptions,
}) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [{ user }, stateDispatch] = useAppContext();

  const { putSeason, deleteSeason } = useSeasonService();
  const { displayNotification } = useNotification();
  const { displayModal, closeModal } = useModal();

  const handleDisplayError = (error, backupType) => {
    displayNotification({
      message: t(error?.response?.data?.code
        ? `code_errors.${error.response.data.code}`
        : `common.${backupType}`),
      color: 'error',
    });
  };

  const { isLoading: isDeleteLoading, mutate: deleteSeasonMutate } = useMutation({
    mutationFn: deleteSeason,
    onError: (error) => {
      handleDisplayError(error, 'deleteError');
      closeModal();
    },
    onSuccess: (res, values) => {
      if (res?.status === 204) {
        setSeasons((prev) => prev.filter((season) => season.id !== values.seasonId));
        closeModal();
      }
    },
  });

  useEffect(() => {
    stateDispatch({ type: 'SET_MODAL_QUERY_LOADING', payload: isDeleteLoading ? 'deleteSeason' : null });
  }, [isDeleteLoading]);

  const { isLoading: isPutLoading, mutate: putSeasonMutate } = useMutation({
    mutationFn: putSeason,
    onError: (error, values) => {
      handleDisplayError(error, 'saveError');

      setSeasons((prev) => prev.map((season) => {
        if (season.id === values.seasonId) {
          return {
            ...season,
            startDate: values.prevValues.startDate,
            type: values.prevValues.type,
          };
        }

        return season;
      }));
    },
  });

  const columns = [
    { key: 'period', title: t('seasons.table.columns.period'), isSortable: false },
    { key: 'season', title: t('seasons.table.columns.season'), isSortable: false },
    { key: 'actions', title: t('seasons.table.columns.actions'), isSortable: false },
  ].filter((col) => col.key !== 'actions' || isGranted(user, [SCOPE.delete_ownEst_season]));

  const handleSeasonChange = ({ seasonId, startDate, type }) => {
    setSeasons((prev) => prev.map((season) => {
      if (season.id === seasonId) {
        return { ...season, startDate, type };
      }

      return season;
    }));

    const updatedSeason = seasons?.find((season) => season.id === seasonId);
    if (updatedSeason && (updatedSeason.type !== type || updatedSeason.startDate !== startDate)) {
      putSeasonMutate({
        seasonId,
        type,
        startDate,
        prevValues: { type: updatedSeason.type, startDate: updatedSeason.startDate },
      });
    }
  };

  const handleDeleteConfirm = (season) => {
    displayModal({
      maxWidth: 520,
      title: t('seasons.table.delete.confirm.title'),
      content: (
        <p
          dangerouslySetInnerHTML={{
            __html: t(
              'seasons.table.delete.confirm.text',
              {
                date: format(dayId.getDateFromDayId(season.startDate), 'dd/MM/yyyy'),
                type: t(`seasons.short.${season.type.toLowerCase()}`).toLowerCase(),
              },
            ),
          }}
        />
      ),
      buttons: [
        {
          label: t('common.cancel'),
          theme: 'inverted',
        },
        {
          label: t('seasons.table.delete.confirm.yes'),
          onClick: () => deleteSeasonMutate({ seasonId: season.id }),
          queryName: 'deleteSeason',
        },
      ],
    });
  };

  const getSeasonRow = (season, index) => (omit({
    id: index,
    period: (
      <div className="input_max small">
        <DatePickerInput
          id={`start_${season.id}`}
          label={t('seasons.table.columns.period')}
          date={dayId.getDateFromDayId(season.startDate)}
          setDate={(date) => handleSeasonChange({
            seasonId: season.id,
            startDate: dayId.getDayIdFromDate(date),
            type: season.type,
          })}
          disabled={isPutLoading || isDeleteLoading || !isGranted(user, [SCOPE.update_ownEst_season])}
          minDate={startOfYear(new Date())}
          maxDate={endOfYear(new Date())}
        />
      </div>
    ),
    season: (
      <div className="input_max small">
        <SelectField
          id={`type_${season.id}`}
          name={`type_${season.id}`}
          options={seasonOptions}
          label={t('seasons.table.columns.season')}
          value={seasonOptions?.find((option) => option.value === season.type)}
          onChange={(option) => handleSeasonChange({
            seasonId: season.id,
            startDate: season.startDate,
            type: option?.value,
          })}
          disabled={isPutLoading || isDeleteLoading || !isGranted(user, [SCOPE.update_ownEst_season])}
          clearable={false}
        />
      </div>
    ),
    actions: (
      <div>
        <button
          type="button"
          className="d-flex a-center p-10 c-error-500 c-pointer hover-dark fw-500 fs-16"
          onClick={() => handleDeleteConfirm(season)}
        >
          <TrashIcon className="m-r-10" width={20} height={20} />
          {t('seasons.table.delete.button')}
        </button>
      </div>
    ),
  }, !isGranted(user, [SCOPE.delete_ownEst_season]) ? ['actions'] : []));

  const seasonsByYear = useMemo(() => (
    seasons?.filter((season) => (
      getYear(dayId.getDateFromDayId(season.startDate)) === year
    )) || []
  ), [year, seasons]);

  return (
    (isFetching) ? (
      <>
        <SkeletonLoader height={60} width={230} className="m-b-20" radius={10} />
        <SkeletonLoader height={60} className="m-b-20" radius={10} />
        <SkeletonLoader height={300} radius={10} />
      </>
    ) : (
      seasons?.length ? (
        seasonsByYear?.length ? (
          isMobile ? (
            <div className="m-t-20">
              {seasonsByYear.map((season, index) => (
                <div key={index} className={styles.mobile_row}>
                  {Object.values(getSeasonRow(season, index))
                    .filter((_, key) => key !== 0)
                    .map((item) => (
                      <div className={styles.mobile_row_field}>{item}</div>
                    ))}
                </div>
              ))}
            </div>
          ) : (
            <GenericTable
              columns={columns}
              rows={seasonsByYear.map((role, index) => getSeasonRow(role, index))}
            />
          )
        ) : (
          <p className="m-t-20 c-neutral-03">
            {t('seasons.table.empty.year', { year })}
          </p>
        )
      ) : (
        <EmptyList
          imageTheme="products"
          title={t('seasons.table.empty.title')}
          text={
            `${t('seasons.table.empty.text')} ${isGranted(
              user,
              [SCOPE.create_ownEst_season],
            )
              ? t('seasons.table.empty.add')
              : ''}`
          }
        />
      )
    )
  );
}

SeasonsTable.propTypes = {
  year: PropTypes.number.isRequired,
  seasons: PropTypes.arrayOf(PropTypes.shape({})),
  setSeasons: PropTypes.func,
  isFetching: PropTypes.bool,
  seasonOptions: PropTypes.arrayOf(PropTypes.shape({})),
};

SeasonsTable.defaultProps = {
  seasons: [],
  setSeasons: () => {},
  isFetching: false,
  seasonOptions: [],
};

export default SeasonsTable;
